import { ColumnDef } from "@tanstack/react-table";
import { TrashAltIcon } from "assets/icons";
import { useLanguage } from "hooks";
import { t } from "i18next";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from "react";
import {
  ItemsTable,
  Status,
  TableItemActionsControl,
  TableItemStatusControl
} from "ui";

import { useCouponStatusChange } from "../../../apis";
import {
  COUPON_STATUSES_LIST,
  CouponStatus,
  CouponType
} from "../../../constants";
import { CouponItem } from "../../../coupons.types";
import { SASCouponsTableProps } from "./SASCouponsTable.types";

import styles from "./SASCouponsTable.module.scss";

const SASCouponsTable = ({
  coupons,
  sorting,
  setSorting,
  isLoading,
  onDeleteCoupon
}: SASCouponsTableProps) => {
  const changeCouponStatus = useCouponStatusChange(
    CouponType.SpendAndSaveCoupon
  );
  const { currentLanguage } = useLanguage();

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);
  const [openedItemStatusesIndex, setOpenedItemStatusesIndex] = useState<
    number | null
  >(null);

  const statusesList: Status[] = COUPON_STATUSES_LIST.map((orStatus) => ({
    ...orStatus,
    onClick: () => {
      if (openedItemStatusesIndex !== null) {
        changeCouponStatus.mutate({
          couponId: coupons[openedItemStatusesIndex].couponId,
          action:
            orStatus.value === CouponStatus.Active ? "activate" : "deactivate"
        });
        setOpenedItemStatusesIndex(null);
      }
    }
  }));

  const columns: Array<ColumnDef<CouponItem, CouponItem[]>> = useMemo(
    () => [
      {
        header: t("Coupon Name"),
        enableSorting: false,
        size: 25,
        minSize: 25,
        cell: ({ row }) => (
          <p className={styles.table_boldText}>{coupons[row.index].code}</p>
        )
      },
      {
        header: t("Cart value"),
        enableSorting: false,
        size: 25,
        minSize: 25,
        cell: ({ row }) =>
          `${coupons[row.index].requirements.cartAmount?.toLocaleString("en-US") ?? "-"} FCFA`
      },
      {
        header: t("Discount"),
        enableSorting: false,
        size: 25,
        minSize: 25,
        cell: ({ row }) => {
          const coupon = coupons[row.index];
          return coupon.discountPercentage
            ? `${coupon.discountPercentage}%`
            : `${coupon.discountAmount?.toLocaleString("en-US")} FCFA`;
        }
      },
      {
        header: t("Status"),
        size: 18,
        minSize: 18,
        cell: ({ row }) => (
          <TableItemStatusControl
            rowIndex={row.index}
            activeStatusIndex={
              coupons[row.index].status === CouponStatus.Active ? 0 : 1
            }
            openedItemStatusesIndex={openedItemStatusesIndex}
            setOpenedItemStatusesIndex={setOpenedItemStatusesIndex}
            statusesList={statusesList}
          />
        )
      },
      {
        accessorKey: "dotsColumn",
        header: () => null,
        size: 2,
        minSize: 2,
        cell: ({ row }) => (
          <TableItemActionsControl
            actionsList={[
              {
                label: "Delete",
                Icon: <TrashAltIcon fill={"#ff0033"} width={18} height={18} />,
                labelColor: "#ff0033",
                onClick: () => onDeleteCoupon(coupons[row.index].couponId)
              }
            ]}
            rowIndex={row.index}
            setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
            openedItemOptionsIndex={openedItemOptionsIndex}
          />
        ),
        enableSorting: false
      }
    ],
    [coupons, openedItemOptionsIndex, openedItemStatusesIndex, currentLanguage]
  );

  return (
    <>
      <ItemsTable
        columns={columns}
        data={coupons}
        sorting={sorting}
        setSorting={setSorting}
        isLoading={isLoading}
        className={styles.table}
      />
    </>
  );
};

export default SASCouponsTable;
